const langList = import.meta.glob('/node_modules/quasar/lang/*.js')
import { type FieldLocalizedMessage, type Locale } from '@/admin/types/locale'
import { type i18nMessagesDTOV2 } from '@/common/types/i18n'
import { Quasar } from 'quasar'
export default function useLocaleUtils() {
  /**
   * To update quasar language dynamically : https://quasar.dev/options/quasar-language-packs#dynamical-ssr-
   * Check also https://quasar.dev/options/app-internationalization/#detecting-locale
   * Warning: If you use Quasar’s set method (Quasar.lang.set()), this will not be reflected by Quasar’s getLocale above.
   * The reason for this is that getLocale() will always return the users locale (based on browser settings).
   * The set() method refers to Quasars internal locale setting which is used to determine which language file to use.
   * If you would like to see which language has been set using set() you can use Quasar.lang.props.isoName.
   */
  async function changeQuasarLanguage(locale: string) {
    const localeQuasarFormat = locale.replace('_', '-')
    try {
      for (const targetLocale of [localeQuasarFormat, localeQuasarFormat.substring(0, 2)]) {
        const langModule = `/node_modules/quasar/lang/${targetLocale}.js`
        if (langModule in langList) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const lang: any = await langList[langModule]()
          Quasar.lang.set(lang.default)
          break
        }
      }
    } catch {
      console.warn("User language doesn't exist in Quasar. Loaded 'en-US' language by default")
    }
  }

  function getBrowserLocal(): string {
    // get default navigator local matching the iso format : [lang]_[country]
    for (const language of navigator.languages) {
      if (language.match(/^[a-z]{2}-[A-Z]{2}$/)) {
        return language
      }
    }
    return navigator.language
  }

  return { changeQuasarLanguage, getBrowserLocal }
}

export function localizedFieldsToI18nApiMessages(translations: FieldLocalizedMessage[], key: string): i18nMessagesDTOV2[] {
  return translations.map((f) => {
    return {
      locale: f.locale,
      key,
      message: f.message,
    }
  })
}

export const getFieldLocalizedMessage = (translationsNames: i18nMessagesDTOV2[], locales: Locale[]): FieldLocalizedMessage[] => {
  const list: FieldLocalizedMessage[] = []
  for (const locale of locales) {
    list.push({
      name: 'name',
      locale: locale.code,
      message: translationsNames.find((f) => f.locale === locale.code)?.message ?? '',
      displayOrder: 1,
    })
  }
  return list
}
